/**
 * V9.11 — Admin : créer une fédération ou un club avec attribution d'un
 * email de gestionnaire (pending_manager_email).
 *
 * Workflow :
 *  - Admin remplit le formulaire (nom + email manager optionnel).
 *  - À la création, si un compte avec cet email existe déjà → owner immédiat.
 *  - Sinon, à la prochaine connexion de cet email → owner automatiquement attribué.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Plus, Mail, Flag, Users, CheckCircle2, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { showError } from "@/lib/errors";
import { EmptyState } from "@/components/EmptyState";

const initialForm = {
  name: "",
  pending_manager_email: "",
  discipline: "",
  description: "",
  contact_email: "",
  website: "",
};

function CreateEntityForm({ kind, onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        discipline: form.discipline.trim() || null,
        description: form.description.trim() || null,
        contact_email: form.contact_email.trim() || null,
        website: form.website.trim() || null,
        pending_manager_email: form.pending_manager_email.trim() || null,
      };
      const endpoint = kind === "federation" ? "/admin/federations" : "/admin/clubs";
      const { data } = await api.post(endpoint, payload);
      const immediate = data.immediate_owner_id;
      if (immediate) {
        toast.success(`Fiche créée. Le compte ${form.pending_manager_email} est immédiatement gestionnaire.`);
      } else if (form.pending_manager_email) {
        toast.success(`Fiche créée. ${form.pending_manager_email} deviendra gestionnaire à sa première connexion.`);
      } else {
        toast.success("Fiche créée.");
      }
      setForm(initialForm);
      onCreated?.();
    } catch (err) {
      showError(err, { fallback: "Création impossible." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 sm:p-5">
      <h2 className="font-display font-semibold text-base mb-3">
        {kind === "federation" ? "Créer une fédération" : "Créer un club"}
      </h2>
      <form onSubmit={submit} className="space-y-3" data-testid={`create-${kind}-form`}>
        <div>
          <label className="text-xs font-medium">Nom <span className="text-destructive">*</span></label>
          <Input value={form.name} onChange={(e) => upd({ name: e.target.value })} placeholder={kind === "federation" ? "Fédération de Surf de Polynésie" : "Tahiti Surf Club"} data-testid={`${kind}-name-input`} />
        </div>
        <div>
          <label className="text-xs font-medium">Discipline</label>
          <Input value={form.discipline} onChange={(e) => upd({ discipline: e.target.value })} placeholder="surf, va'a, jiu-jitsu..." />
        </div>
        <div>
          <label className="text-xs font-medium">Description courte</label>
          <Textarea value={form.description} onChange={(e) => upd({ description: e.target.value })} rows={2} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium">Email de contact public</label>
            <Input type="email" value={form.contact_email} onChange={(e) => upd({ contact_email: e.target.value })} placeholder="contact@fede.pf" />
          </div>
          <div>
            <label className="text-xs font-medium">Site web</label>
            <Input value={form.website} onChange={(e) => upd({ website: e.target.value })} placeholder="https://..." />
          </div>
        </div>
        <div className="rounded-md border border-dashed border-[hsl(var(--aito-gold))]/40 bg-[hsl(var(--aito-gold))]/5 p-3">
          <label className="text-xs font-medium flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-[hsl(var(--aito-gold))]" />
            Email du futur gestionnaire <span className="text-muted-foreground font-normal">(optionnel)</span>
          </label>
          <Input
            type="email"
            value={form.pending_manager_email}
            onChange={(e) => upd({ pending_manager_email: e.target.value })}
            placeholder={kind === "federation" ? "president@federation.pf" : "manager@club.pf"}
            data-testid={`${kind}-manager-email-input`}
          />
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Cet email deviendra automatiquement gestionnaire de la fiche dès qu'il/elle se connectera
            (s'il a déjà un compte, l'attribution est immédiate). Tu peux transmettre l'URL de la fiche à cette personne.
          </p>
        </div>
        <div className="pt-2">
          <Button type="submit" disabled={busy} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid={`${kind}-submit-btn`}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
            Créer la fiche
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PendingManagersList({ refreshKey }) {
  const [data, setData] = useState({ federations: [], clubs: [], count: 0 });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/pending-managers");
      setData(d);
    } catch (e) {
      showError(e, { fallback: "Impossible de charger la liste." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  const renderRow = (item, kind) => (
    <li key={item.id} className="py-2 flex items-start gap-3 min-w-0 border-b last:border-b-0">
      <div className={`h-9 w-9 shrink-0 rounded-md flex items-center justify-center ${kind === "federation" ? "bg-[hsl(var(--aito-lagoon))]/15 text-[hsl(var(--aito-lagoon))]" : "bg-[hsl(var(--aito-gold))]/15 text-[hsl(var(--aito-gold))]"}`}>
        {kind === "federation" ? <Flag className="h-4 w-4" /> : <Users className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Link to={kind === "federation" ? `/federations/${item.slug || item.id}` : `/clubs/${item.slug || item.id}`} className="font-medium text-sm hover:underline break-words">
            {item.name}
          </Link>
          {item.owner_user_id ? (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Attribué</Badge>
          ) : (
            <Badge variant="outline" className="border-amber-500/40 text-amber-700 text-[10px]"><Clock className="h-3 w-3 mr-1" />En attente</Badge>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 break-all">
          <Mail className="h-3 w-3 inline-block mr-1" />{item.pending_manager_email}
          {item.pending_manager_invited_at && <> · invité le {new Date(item.pending_manager_invited_at).toLocaleDateString("fr-FR")}</>}
        </div>
      </div>
    </li>
  );

  return (
    <Card className="p-4 sm:p-5">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="font-display font-semibold text-base">Gestionnaires en attente <span className="text-muted-foreground text-xs font-normal">({data.count || 0})</span></h2>
        <Button variant="outline" size="sm" onClick={reload}>Actualiser</Button>
      </div>
      {loading ? (
        <div className="text-center text-muted-foreground py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : (data.federations.length + data.clubs.length) === 0 ? (
        <EmptyState icon={Info} title="Aucun gestionnaire en attente." description="Crée une fédération ou un club avec un email pour pré-attribuer la gestion." />
      ) : (
        <div className="space-y-4">
          {data.federations.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Fédérations ({data.federations.length})</h3>
              <ul>{data.federations.map((f) => renderRow(f, "federation"))}</ul>
            </div>
          )}
          {data.clubs.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Clubs ({data.clubs.length})</h3>
              <ul>{data.clubs.map((c) => renderRow(c, "club"))}</ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function AdminEntitiesPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <h1 className="font-display font-bold text-2xl sm:text-3xl">Créer une fédération ou un club</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Crée la fiche d'une fédération ou d'un club et pré-attribue la gestion à un email — la personne deviendra automatiquement gestionnaire de la fiche à sa première connexion.
      </p>

      <Alert className="mb-5 border-[hsl(var(--aito-gold))]/40 bg-[hsl(var(--aito-gold))]/5">
        <Info className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
        <AlertTitle>Comment ça marche ?</AlertTitle>
        <AlertDescription className="text-xs">
          1. Remplis le formulaire ci-dessous. 2. Saisis l'email du futur gestionnaire (président, secrétaire…).
          3. Transmets-lui le lien de la fiche. 4. Dès qu'il/elle se connectera sur AITO.PF avec cet email,
          il/elle deviendra automatiquement propriétaire de la fiche et pourra la compléter.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="federation">
        <TabsList>
          <TabsTrigger value="federation" data-testid="tab-create-fed"><Flag className="h-3.5 w-3.5 mr-1.5" />Fédération</TabsTrigger>
          <TabsTrigger value="club" data-testid="tab-create-club"><Users className="h-3.5 w-3.5 mr-1.5" />Club</TabsTrigger>
        </TabsList>
        <TabsContent value="federation" className="mt-4">
          <CreateEntityForm kind="federation" onCreated={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="club" className="mt-4">
          <CreateEntityForm kind="club" onCreated={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <PendingManagersList refreshKey={refreshKey} />
      </div>
    </div>
  );
}
